import {useEffect,useRef,useState} from 'react';
import {ChartNoAxesCombined,ChevronLeft,Info,LoaderCircle,RefreshCw} from 'lucide-react';
import type {VisualizeEntitlement} from '@reados/contracts';
import {VISUALIZE_PLUGIN_ID,VISUALIZE_PLUGIN_VERSION} from '@reados/contracts';
import {ApiError,errorMessage} from '../api';
import {ComposerPopover} from './ComposerPopover';
import {PluginGlyph} from './PluginGlyph';
import './visualize-panel.css';

export interface VisualizePanelProps {
  value?:VisualizeEntitlement;
  busy?:boolean;
  error?:string;
  onToggle?:(enabled:boolean)=>Promise<VisualizeEntitlement>;
  onRefresh?:()=>Promise<unknown>;
  onSubscribe?:()=>void;
  onSelect?:()=>void;
}
const summary='创建交互式图表、地图、模拟和 3D 视图';

/** Mirrors the iPad picker: selection, a separate info action, and an explicit switch. */
export function VisualizePanel({value,busy=false,error='',onToggle,onRefresh,onSubscribe,onSelect}:VisualizePanelProps){
  const [detail,setDetail]=useState(false),[purchase,setPurchase]=useState(false),[failure,setFailure]=useState(''),[pending,setPending]=useState(false);
  const active=useRef(true);useEffect(()=>{active.current=true;return()=>{active.current=false;};},[]);
  const locked=value?.allowed===false,working=pending||busy;
  const switchRef=useRef<HTMLButtonElement>(null),restoreSwitchFocus=useRef(false);
  useEffect(()=>{if(!working&&restoreSwitchFocus.current){restoreSwitchFocus.current=false;switchRef.current?.focus({preventScroll:true});}},[working]);
  const planNames=value?.requiredPlanNames??value?.requiredPlans.map(id=>({basic:'Basic',pro:'Pro'}[id]??id))??['Basic','Pro'];
  const accessCopy=planNames.length?`Visualize 需要 ${planNames.join(' 或 ')} 套餐`:'当前套餐暂未开放 Visualize';
  async function change(){
    if(!value||working)return;
    if(!value.allowed){setPurchase(true);return;}
    if(!onToggle)return;
    restoreSwitchFocus.current=document.activeElement===switchRef.current;
    setPending(true);setFailure('');
    try{await onToggle(!value.enabled);}
    catch(reason){if(active.current){if(reason instanceof ApiError&&reason.code==='FEATURE_SUBSCRIPTION_REQUIRED'){setFailure('');setPurchase(true);}else setFailure(errorMessage(reason));}}
    finally{if(active.current)setPending(false);}
  }
  const toggle=<button ref={switchRef} type="button" role="switch" aria-checked={!!value?.enabled} aria-label="启用 Visualize 插件" className="visualize-switch" disabled={!value||working||!onToggle} onClick={()=>void change()}><span/></button>;
  return <section className="visualize-panel" aria-label="Visualize 插件" aria-busy={working}>
    {detail?<>
      <header><button type="button" onClick={()=>setDetail(false)}><ChevronLeft size={16}/>返回</button><strong>插件详情</strong></header>
      <div className="visualize-detail-heading"><ChartNoAxesCombined size={32}/><div><h3>Visualize</h3><p>{summary}</p></div></div>
      <p>在对话中探索数据、调整参数和查看变化。支持交互图表、空间模型与动画演示。</p>
      <div className="visualize-setting"><span>启用插件</span>{toggle}</div>
      <h4>技能 · 1</h4><p><strong>visualize</strong><br/>{summary}。启用插件后，Readex 可按问题需要使用此技能。</p>
      <h4>信息</h4><dl><dt>开发者</dt><dd>ReadOS</dd><dt>版本</dt><dd>{VISUALIZE_PLUGIN_VERSION}</dd><dt>标识</dt><dd>{VISUALIZE_PLUGIN_ID}</dd></dl>
    </>:<div className="visualize-picker-row">
      <button type="button" className="visualize-select" disabled={onSelect?!value?.enabled:false} onClick={()=>onSelect?onSelect():setDetail(true)}>
        <ChartNoAxesCombined size={20}/><span><strong>Visualize</strong><small>{summary}</small></span>
      </button>
      <button type="button" className="visualize-info" aria-label="查看 Visualize 插件详情" onClick={()=>setDetail(true)}><Info size={18}/></button>
      {toggle}
    </div>}
    {!value&&!error&&<p role="status"><LoaderCircle size={14} className="spin"/>正在读取插件设置…</p>}
    {locked&&!purchase&&<p className="visualize-access">{accessCopy}</p>}
    {(failure||error)&&!purchase&&<div className="visualize-error" role="alert">{failure||error}{onRefresh&&<button type="button" disabled={working} onClick={()=>{setFailure('');void onRefresh();}}><RefreshCw size={14}/>重试</button>}</div>}
    {purchase&&<div className="visualize-purchase" role="alert"><p>{accessCopy}，开通后即可启用。</p><div><button type="button" onClick={()=>setPurchase(false)}>暂不购买</button>{onSubscribe&&planNames.length>0&&<button type="button" onClick={onSubscribe}>查看 {planNames.join(' / ')} 套餐</button>}</div></div>}
  </section>;
}

export function VisualizeLibraryButton(props:VisualizePanelProps){
  const [open,setOpen]=useState(false),anchor=useRef<HTMLButtonElement>(null);
  const close=()=>{setOpen(false);anchor.current?.focus({preventScroll:true});};
  return <><button ref={anchor} type="button" className="ipad-library-control" title="插件" aria-label="资料库插件" aria-haspopup="dialog" aria-expanded={open} onClick={()=>{setOpen(value=>!value);void props.onRefresh?.();}}><PluginGlyph/></button>
    {open&&<ComposerPopover title="插件" kind="plugins" anchor={anchor} width={390} onClose={close}><h3 className="visualize-library-title">插件</h3><VisualizePanel {...props} onSubscribe={()=>{close();props.onSubscribe?.();}}/></ComposerPopover>}
  </>;
}
